import React from "react";
import classNames from "classnames";

// types
import { RadioInputProps } from "../../../types/components/dynamicForms/dynamicField";

/**
 * Presentational radio-group primitive. One selectable row per option, the
 * whole row is the click/label target, native <input type="radio"> drives
 * selection and keyboard nav. No react-hook-form awareness — value in, value
 * out, matching every other primitive's BaseControlProps contract.
 *
 * value === "" means nothing selected (no row checked). `icon` is a ReactNode
 * the caller supplies; this primitive is agnostic about where it comes from and
 * renders nothing when it's absent, so it's reusable where no icon exists.
 */
const RadioInput = ({
  id,
  value,
  onChange,
  onBlur,
  options,
  disabled,
  invalid,
  describedById,
}: RadioInputProps) => {
  return (
    <div
      className={classNames("bwp-radio-group", {
        "bwp-radio-group--state-error": invalid,
      })}
      role="radiogroup"
      aria-invalid={invalid || undefined}
      aria-describedby={describedById}
    >
      {options.map((option) => {
        const checked = option.value === value;
        const optionId = `${id}-${option.value}`;
        return (
          <label
            key={option.value}
            htmlFor={optionId}
            className={classNames("bwp-radio", {
              "bwp-radio--checked": checked,
              "bwp-radio--disabled": disabled,
            })}
          >
            <input
              id={optionId}
              type="radio"
              name={id}
              className="bwp-radio__input"
              value={option.value}
              checked={checked}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
            />
            {option.icon !== undefined && (
              <span className="bwp-radio__icon" aria-hidden="true">
                {option.icon}
              </span>
            )}
            <span className="bwp-radio__label">{option.label}</span>
            <span className="bwp-radio__control" aria-hidden="true" />
          </label>
        );
      })}
    </div>
  );
};

export default RadioInput;